import { useLoader } from '@/hooks/use-loader';
import { unwrap } from '@/lib/api-errors';
import { errorText } from '@/stores/mutation-result';
import { getWorkUsage } from '@/lib/usage-api';

/** Tabs send their counted minutes about once a minute (hooks/use-activity-tracking.ts). */
const PEOPLE_TIME_POLL_MS = 60_000;

/**
 * A task's or a run's agent usage (GET /api/usage), and on a task people's time too (T-102). `refreshKey` loads it again when it changes (the
 * newest run's status: its T3 runs arrive a few minutes after it finishes).
 */
export function useWorkUsage(by: { task_id: string } | { run_id: string } | null, projectSlug: string | null, refreshKey: unknown = null) {
  const id = by ? ('task_id' in by ? `task:${by.task_id}` : `run:${by.run_id}`) : null;
  // Rejects on a failed read, so a failed poll keeps the last answer on screen (and the error beside it).
  const loader = useLoader(async (signal) => unwrap(await getWorkUsage(by!, { signal, project: projectSlug ?? undefined })), [id, projectSlug, refreshKey], {
    enabled: Boolean(by && projectSlug),
    // A task's people's time (T-102) grows as people work on it, theirs and other people's: load it again
    // each minute while the tab is shown (useLoader pauses polls while hidden).
    pollMs: by && 'task_id' in by ? PEOPLE_TIME_POLL_MS : undefined,
  });
  return {
    usage: loader.data?.usage,
    /** A task's only (T-102): the time people spent with it open. */
    people: loader.data?.people,
    error: loader.error ? errorText(loader.error, "The server didn't answer.") : null,
    reload: loader.reload,
  };
}
